import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { getPopularCategories } from "@/utils/supabase/categoryHelpers";
import iconMap from "@/components/icons/iconMap";
import { trackHomepageEvent } from "@/services/homepageAnalytics";
import {
  CircleHelp,
} from "lucide-react";

interface Category {
  id: string;
  name: string;
  icon?: string | null;
  created_at?: string | null;
  count?: number;
}

interface CategoryGridProps {
  limit?: number;
}

const CategoryGrid = ({ limit }: CategoryGridProps) => {
  const { t } = useTranslation('homepage');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getPopularCategories(limit || 8);
        setCategories(data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [limit]);

  const renderCategoryIcon = (iconName: string | null | undefined) => {
    if (!iconName) {
      return <span className="text-3xl">📋</span>;
    }
    const LucideIcon = iconMap[iconName];
    if (LucideIcon) {
      return <LucideIcon className="h-7 w-7 text-brand-ink" />;
    }
    return <CircleHelp className="h-7 w-7 text-brand-slate" strokeWidth={1.75} />;
  };

  if (loading) {
    return (
      <section className="py-12 md:py-16" data-test-responsive="category-grid" id="popular-categories">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">
                {t('categories.title')}
              </h2>
              <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">
                {t('categories.description')}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {Array.from({ length: limit || 8 }, (_, index) => (
              <div key={index} className="min-h-[150px] animate-pulse rounded-2xl border border-brand-cloud bg-white p-5 md:p-6">
                <div className="mb-8 flex items-start justify-between">
                  <div className="h-10 w-10 rounded-xl bg-brand-cloud"></div>
                  <div className="h-4 w-4 rounded-full bg-brand-cloud"></div>
                </div>
                <div className="h-4 w-3/4 rounded bg-brand-cloud"></div>
                <div className="mt-3 h-3 w-1/2 rounded bg-brand-cloud"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (categories.length === 0) {
    return (
      <section className="py-12 md:py-16" data-test-responsive="category-grid" id="popular-categories">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">
                {t('categories.title')}
              </h2>
              <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">
                {t('categories.description')}
              </p>
            </div>
          </div>
          <div className="rounded-2xl border border-brand-cloud bg-brand-mist/50 px-6 py-8 text-center text-brand-slate">
            {t('categories.empty')}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16" data-test-responsive="category-grid" id="popular-categories">
      <div className="container mx-auto px-4">
        <div className="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <h2 className="font-heading text-[length:var(--text-h2)] font-semibold text-brand-ink">
              {t('categories.title')}
            </h2>
            <p className="mt-2 max-w-2xl text-[length:var(--text-body)] text-brand-slate">
              {t('categories.description')}
            </p>
          </div>
          {limit && (
            <Link to="/explore">
              <Button variant="outline" className="border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white transition-colors">
                {t('categories.viewAll')}
              </Button>
            </Link>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {categories.map((category) => (
            <Link 
              key={category.id} 
              to={`/explore?category=${encodeURIComponent(category.name)}`}
              onClick={() => trackHomepageEvent('homepage_category_click', {
                category_name: category.name,
              })}
              className="group flex min-h-[150px] flex-col items-center justify-between rounded-2xl border border-brand-cloud bg-white p-5 text-center transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-ink hover:shadow-md md:p-6"
            >
              <div className="flex items-center justify-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-mist">
                  {renderCategoryIcon(category.icon)}
                </div>
              </div>
              <div className="mt-6">
                <h3 className="font-heading text-base font-medium text-brand-ink">{category.name}</h3>
                {category.count != null && (
                  <p className="mt-2 text-[length:var(--text-small)] text-brand-slate">
                    {category.count} {category.count === 1 ? 'provider' : 'providers'}
                  </p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CategoryGrid;
